import React, { useState, useEffect } from "react";
import useReadAllCharterRequests from "../lib/useReadAllCharterRequests";
import useReadCharterRequest from "../lib/useReadCharterRequest";
import useIsChartererMovingForward from "../lib/useIsChartererMovingForward";
import useSelectMeetingDate from "../lib/useSelectMeetingDate";
import useSignVesselNominationAgreement from "../lib/useSignVesselNominationAgreement";
import useSignCharterPartyAgreement from "../lib/useSignCharterPartyAgreement";
import { useToast } from "../../Context/ToastContext";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { calculateWorkflowStep, getNextAction } from "../../Utils/workflowStepCalculator";
import { FiAlertCircle } from "react-icons/fi";
import "./OngoingProcess.css";

const OngoingProcess = () => {
  const { charterRequests, charterRequestsLoading, charterRequestsError } = useReadAllCharterRequests();
  const [selectedRequestId] = useState(null);
  const { charterRequest, charterRequestLoading, charterRequestError } = useReadCharterRequest(selectedRequestId);
  const { moveForward, isLoading: moveForwardLoading, isSuccess: moveForwardSuccess, isError: moveForwardError, error: moveForwardErrorData } = useIsChartererMovingForward();
  const { selectMeetingDate, isLoading: selectDateLoading, isSuccess: selectDateSuccess, isError: selectDateError, error: selectDateErrorData } = useSelectMeetingDate();
  const { signAgreement, isLoading: signAgreementLoading, isSuccess: signAgreementSuccess, isError: signAgreementError, error: signAgreementErrorData } = useSignVesselNominationAgreement();
  const { signAgreement: signCharterPartyAgreement, isLoading: signCharterPartyAgreementLoading, isSuccess: signCharterPartyAgreementSuccess, isError: signCharterPartyAgreementError, error: signCharterPartyAgreementErrorData } = useSignCharterPartyAgreement();
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingRequestId, setPendingRequestId] = useState(null);

  // Meeting date modal state
  const [showMeetingDateModal, setShowMeetingDateModal] = useState(false);
  const [meetingDateRequestId, setMeetingDateRequestId] = useState(null);
  const [meetingDates, setMeetingDates] = useState({
    date1: "",
    date2: ""
  });

  // Sign agreement modal state
  const [showSignAgreementModal, setShowSignAgreementModal] = useState(false);
  const [signAgreementRequestId, setSignAgreementRequestId] = useState(null);
  const [signAgreementType, setSignAgreementType] = useState("vesselNomination"); // 'vesselNomination' | 'charterParty'

  // Handle move forward success and error states
  useEffect(() => {
    if (moveForwardSuccess) {
      showToast("Charter request updated successfully!", "success");
      setShowConfirmationModal(false);
      setPendingRequestId(null);
    }
  }, [moveForwardSuccess, showToast]);

  useEffect(() => {
    if (moveForwardError && moveForwardErrorData) {
      showToast(moveForwardErrorData.message || "Failed to update charter request", "error");
      setShowConfirmationModal(false);
      setPendingRequestId(null);
    }
  }, [moveForwardError, moveForwardErrorData, showToast]);

  // Handle meeting date selection success and error states
  useEffect(() => {
    if (selectDateSuccess) {
      showToast("Meeting dates selected successfully!", "success");
      setShowMeetingDateModal(false);
      setMeetingDateRequestId(null);
      setMeetingDates({ date1: "", date2: "" });
    }
  }, [selectDateSuccess, showToast]);

  useEffect(() => {
    if (selectDateError && selectDateErrorData) {
      showToast(selectDateErrorData.message || "Failed to select meeting dates", "error");
    }
  }, [selectDateError, selectDateErrorData, showToast]);

  // Handle sign agreement success and error states (vessel nomination)
  useEffect(() => {
    if (signAgreementSuccess) {
      showToast("Vessel nomination agreement signed successfully!", "success");
      setShowSignAgreementModal(false);
      setSignAgreementRequestId(null);
    }
  }, [signAgreementSuccess, showToast]);

  useEffect(() => {
    if (signAgreementError && signAgreementErrorData) {
      showToast(signAgreementErrorData.message || "Failed to sign agreement", "error");
      setShowSignAgreementModal(false);
      setSignAgreementRequestId(null);
    }
  }, [signAgreementError, signAgreementErrorData, showToast]);

  // Handle charter party sign success/error
  useEffect(() => {
    if (signCharterPartyAgreementSuccess) {
      showToast("Charter party agreement signed successfully!", "success");
      setShowSignAgreementModal(false);
      setSignAgreementRequestId(null);
    }
  }, [signCharterPartyAgreementSuccess, showToast]);

  useEffect(() => {
    if (signCharterPartyAgreementError && signCharterPartyAgreementErrorData) {
      showToast(signCharterPartyAgreementErrorData.message || "Failed to sign charter party agreement", "error");
    }
  }, [signCharterPartyAgreementError, signCharterPartyAgreementErrorData, showToast]);

  // Function to handle move forward action
  const handleMoveForward = (requestId) => {
    setPendingRequestId(requestId);
    setShowConfirmationModal(true);
  };

  // Function to confirm move forward
  const confirmMoveForward = () => {
    if (pendingRequestId) {
      moveForward({
        requestId: pendingRequestId,
        body: {
          processed: true,
          acceptPrice: true // Charterer accepts the vessel owner's price
        }
      });
    }
  };

  // Function to cancel move forward
  const cancelMoveForward = () => {
    setShowConfirmationModal(false);
    setPendingRequestId(null);
  };

  // Function to handle meeting date selection
  const handleSelectMeetingDate = (requestId) => {
    setMeetingDateRequestId(requestId);
    setShowMeetingDateModal(true);
  };

  // // Function to handle meeting date input changes
  // const handleMeetingDateChange = (field, value) => {
  //   setMeetingDates(prev => ({
  //     ...prev,
  //     [field]: value
  //   }));
  // };

  // Function to submit meeting dates
  const submitMeetingDates = () => {
    if (!meetingDates.date1 || !meetingDates.date2) {
      showToast("Please select both meeting dates", "error");
      return;
    }

    if (meetingDateRequestId) {
      selectMeetingDate({
        requestId: meetingDateRequestId,
        body: {
          date1: new Date(meetingDates.date1).toISOString(),
          date2: new Date(meetingDates.date2).toISOString()
        }
      });
    }
  };

  // Function to cancel meeting date selection
  const cancelMeetingDateSelection = () => {
    setShowMeetingDateModal(false);
    setMeetingDateRequestId(null);
    setMeetingDates({ date1: "", date2: "" });
  };

  // Function to handle sign agreement action (type-aware)
  const handleSignAgreement = (requestId, type) => {
    setSignAgreementRequestId(requestId);
    setSignAgreementType(type);
    setShowSignAgreementModal(true);
  };

  // Function to confirm sign agreement (vessel nomination or charter party)
  const confirmSignAgreement = () => {
    if (!signAgreementRequestId) return;
    const payload = { requestId: signAgreementRequestId, body: { signed: true } };
    if (signAgreementType === "charterParty") {
      signCharterPartyAgreement(payload);
    } else {
      signAgreement(payload);
    }
  };

  // Function to cancel sign agreement
  const cancelSignAgreement = () => {
    setShowSignAgreementModal(false);
    setSignAgreementRequestId(null);
  };

  // Function to get status class based on charter request data
  const getStatusClass = (request) => {
    // You can customize this based on your status logic
    if (request.status === "pending") return "status-review";
    if (request.status === "approved") return "status-approval";
    if (request.status === "negotiating") return "status-negotiation";
    if (request.status === "contract") return "status-contract";
    return "status-verification";
  };

  // Function to calculate progress based on 12-step display mapping
  const getDisplayStep = (apiStep) => {
    switch (apiStep) {
      case 1: return 1; // charterRequest => charterer
      case 2: return 2; // processCharterRequest => admin
      case 3: return 3; // charterResponse => vessel owner
      case 4: return 4; // processCharterResponse => admin
      case 5: return 5; // isCharterMovingForward => charterer
      case 6: return 6; // processCharterMovingForward => admin (if acceptPrice false)
      case 7: return 7; // selectMeetingDate => charterer and vessel owner
      case 8: return 8; // meeting result saved to database => system
      case 9: return 9; // signVesselNominationAgreement => charterer and vessel owner
      case 10: return 10; // processVesselInspectionResult => admin
      case 11: return 11; // signCharterPartyAgreement => charterer and vessel owner
      case 12: return 12; // processCharterPartyAgreement => admin
      default: return apiStep;
    }
  };

  const calculateProgress = (request) => {
    const apiStep = request.step !== undefined ? request.step : calculateWorkflowStep(request);
    const displayStep = getDisplayStep(apiStep);
    const progress = (displayStep / 12) * 100;
    return Math.round(progress);
  };

  // Function to get next step based on status
  const getNextStep = (request) => {
    const apiStep = request.step !== undefined ? request.step : calculateWorkflowStep(request);
    const displayStep = getDisplayStep(apiStep);
    if (displayStep >= 12) return "Completed";

    const nextAction = getNextAction(apiStep, "charterer");
    return nextAction?.title || "Processing";
  };

  // Function to get estimated completion
  const getEstimatedCompletion = (request) => {
    const startDate = new Date(request.startDate);
    const estimatedDate = new Date(startDate.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 days from start
    return estimatedDate.toLocaleDateString();
  };

  if (charterRequestsLoading) {
    return (
      <div className="ongoing-process-container">
        <h2 className="process-title">Ongoing Applications</h2>
        <div className="process-list">
          {[1, 2, 3].map((i) => (
            <div key={i} className="process-card">
              <Skeleton height={70} style={{ marginBottom: 20 }} />
              <Skeleton height={8} style={{ marginBottom: 8 }} />
              <Skeleton height={14} style={{ marginBottom: 20 }} />
              <div style={{ display: "flex", gap: 40, marginBottom: 24 }}>
                <div style={{ flex: 1 }}>
                  <Skeleton height={12} count={2} style={{ marginBottom: 12 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Skeleton height={12} count={2} style={{ marginBottom: 12 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <Skeleton height={12} count={2} style={{ marginBottom: 12 }} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <Skeleton height={36} width={120} />
                <Skeleton height={36} width={120} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (charterRequestsError) {
    return (
      <div className="ongoing-process-container">
        <h2 className="process-title">Ongoing Applications</h2>
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          justifyContent: "center", 
          padding: "40px 20px",
          textAlign: "center"
        }}>
          <FiAlertCircle size={48} color="#f59e0b" style={{ marginBottom: "12px" }} />
          <p style={{ color: "#374151", fontWeight: "600", fontSize: "18px", margin: "0 0 4px 0" }}>Pending Approval</p>
          <p style={{ color: "#6b7280", fontSize: "14px", margin: "0" }}>Your charter requests are being reviewed.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="ongoing-process-container">
        <h2 className="process-title">Ongoing Applications ({charterRequests.length})</h2>
        
        <div className="process-list">
          {charterRequests.length === 0 ? (
            <div style={{ textAlign: "center", color: "#6B7280", padding: "40px 0" }}>
              No charter requests found.
            </div>
          ) : (
            charterRequests.map((request) => {
              const progress = calculateProgress(request);
              const nextStep = getNextStep(request);
              const estimatedCompletion = getEstimatedCompletion(request);
              const apiStep = request.step !== undefined ? request.step : calculateWorkflowStep(request);
              const displayStep = getDisplayStep(apiStep);
              
              return (
                <div className="process-card" key={request._id}>
                  <div className="process-header">
                    <div className="process-image">
                      <img 
                        src="https://res.cloudinary.com/du44pwjoi/image/upload/v1742471097/csv_klkqpb.png" 
                        alt="Vessel" 
                      />
                    </div>
                    <div className="process-vessel-info">
                      <h3>Charter Request #{request._id.slice(-8)}</h3>
                      <span className="vessel-type">Duration: {request.duration} days</span>
                    </div>
                    <div className={`process-status ${getStatusClass(request)}`}>
                      {nextStep === "Completed" ? "Completed" : (request.status || "Pending")}
                    </div>
                  </div>
                  
                  <div className="process-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">{progress}% Complete</div>
                  </div>
                  
                  <div className="process-details">
                    <div className="detail-col">
                      <div className="detail-item">
                        <span className="detail-label">Start Date:</span>
                        <span className="detail-value">
                          {request.startDate ? new Date(request.startDate).toLocaleDateString() : "N/A"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">{request.duration} days</span>
                      </div>
                    </div>
                    <div className="detail-col">
                      <div className="detail-item">
                        <span className="detail-label">Scope of Work:</span>
                        <span className="detail-value">
                          {request.scopeOfWork ? request.scopeOfWork.substring(0, 30) + "..." : "N/A"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Next Step:</span>
                        <span className="detail-value highlight">{nextStep}</span>
                      </div>
                    </div>
                    <div className="detail-col">
                      <div className="detail-item">
                        <span className="detail-label">Est. Completion:</span>
                        <span className="detail-value">{estimatedCompletion}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Passengers:</span>
                        <span className="detail-value">{request.numberOfPassengers || "N/A"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details Section */}
                  {selectedRequestId === request._id && (
                    <div style={{ 
                      background: "#f8fafc", 
                      borderRadius: 8, 
                      padding: 16, 
                      marginBottom: 20,
                      border: "1px solid #e2e8f0"
                    }}>
                      {charterRequestLoading ? (
                        <div style={{ display: "flex", gap: 40 }}>
                          <div style={{ flex: 1 }}>
                            <Skeleton height={12} count={3} style={{ marginBottom: 12 }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Skeleton height={12} count={3} style={{ marginBottom: 12 }} />
                          </div>
                        </div>
                      ) : charterRequestError ? (
                        <div style={{ color: "#e74c3c" }}>Error: {charterRequestError.message}</div>
                      ) : charterRequest ? (
                        <div style={{ display: "flex", gap: 40 }}>
                          <div style={{ flex: 1 }}>
                            <div className="detail-item">
                              <span className="detail-label">Full Scope of Work:</span>
                              <span className="detail-value">{charterRequest.scopeOfWork}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Origin:</span>
                              <span className="detail-value">
                                {charterRequest.origin ? charterRequest.origin.coordinates.join(", ") : "N/A"}
                              </span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Destination:</span>
                              <span className="detail-value">
                                {charterRequest.destination ? charterRequest.destination.coordinates.join(", ") : "N/A"}
                              </span>
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="detail-item">
                              <span className="detail-label">Technical Requirements:</span>
                              <span className="detail-value">{charterRequest.technicalRequirements || "N/A"}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Security Required:</span>
                              <span className="detail-value">{charterRequest.security ? "Yes" : "No"}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Other Requirements:</span>
                              <span className="detail-value">{charterRequest.otherRequirements || "N/A"}</span>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                  
                  <div className="process-actions">
                    <button 
                      className="process-action-btn view-details"
                      onClick={() => navigate(`/charterer-dashboard/ongoing-process/${request._id}`)}
                    >
                      View Full Details
                    </button>
                    {displayStep === 5 && (
                      <button 
                        className="process-action-btn negotiate"
                        onClick={() => handleMoveForward(request._id)}
                        disabled={moveForwardLoading}
                      >
                        {moveForwardLoading ? "Processing..." : "Move Forward"}
                      </button>
                    )}
                    {displayStep === 7 && (
                      <button 
                        className="process-action-btn upload"
                        onClick={() => handleSelectMeetingDate(request._id)}
                        disabled={selectDateLoading}
                      >
                        {selectDateLoading ? "Processing..." : "Select Meeting Date"}
                      </button>
                    )}
                    {displayStep === 9 && (
                      <button 
                        className="process-action-btn contract"
                        onClick={() => handleSignAgreement(request._id, 'vesselNomination')}
                        disabled={signAgreementLoading}
                      >
                        {signAgreementLoading ? "Processing..." : "Sign Vessel Nomination"}
                      </button>
                    )}
                    {displayStep === 11 && (
                      <button 
                        className="process-action-btn contract"
                        onClick={() => handleSignAgreement(request._id, 'charterParty')}
                        disabled={signCharterPartyAgreementLoading}
                      >
                        {signCharterPartyAgreementLoading ? "Processing..." : "Sign Charter Party"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
          }}>
            <h3 style={{
              margin: "0 0 16px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
            }}>
              Confirm Action
            </h3>
            <p style={{
              margin: "0 0 24px 0",
              fontSize: "14px",
              color: "#6B7280",
              lineHeight: "1.5",
            }}>
              Are you sure you want to move forward with this charter request? This action cannot be undone.
            </p>
            <div style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
            }}>
              <button
                onClick={cancelMoveForward}
                disabled={moveForwardLoading}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  background: "white",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#F9FAFB"}
                onMouseOut={(e) => e.target.style.backgroundColor = "white"}
              >
                Cancel
              </button>
              <button
                onClick={confirmMoveForward}
                disabled={moveForwardLoading}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  background: moveForwardLoading ? "#9CA3AF" : "#2563EB",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: moveForwardLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  if (!moveForwardLoading) {
                    e.target.style.backgroundColor = "#1D4ED8";
                  }
                }}
                onMouseOut={(e) => {
                  if (!moveForwardLoading) {
                    e.target.style.backgroundColor = "#2563EB";
                  }
                }}
              >
                {moveForwardLoading ? "Processing..." : "Yes, Move Forward"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Date Selection Modal */}
      {showMeetingDateModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
          }}>
            <h3 style={{
              margin: "0 0 16px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
            }}>
              Select Meeting Dates
            </h3>
            <p style={{
              marginBottom: "20px",
              color: "#6B7280",
              fontSize: "14px",
              lineHeight: "1.5",
            }}>
              Please select 2 preferred meeting dates from the available options below.
            </p>
            
            {charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated ? (
              <>
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "8px",
                    }}>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1 || 
                                 meetingDates.date2 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ 
                                ...prev, 
                                date1: charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1 
                              }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ 
                                ...prev, 
                                date2: charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1 
                              }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1 && 
                                  meetingDates.date2 !== charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1)}
                        style={{ marginRight: "8px" }}
                      />
                      First Meeting Date: {new Date(charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date1).toLocaleString()}
                    </label>
                  </div>
                  
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "8px",
                    }}>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2 || 
                                 meetingDates.date2 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ 
                                ...prev, 
                                date1: charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2 
                              }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ 
                                ...prev, 
                                date2: charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2 
                              }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2 && 
                                  meetingDates.date2 !== charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2)}
                        style={{ marginRight: "8px" }}
                      />
                      Second Meeting Date: {new Date(charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date2).toLocaleString()}
                    </label>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "8px",
                    }}>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3 || 
                                 meetingDates.date2 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ 
                                ...prev, 
                                date1: charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3 
                              }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ 
                                ...prev, 
                                date2: charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3 
                              }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3 && 
                                  meetingDates.date2 !== charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3)}
                        style={{ marginRight: "8px" }}
                      />
                      Third Meeting Date: {new Date(charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated.date3).toLocaleString()}
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ color: "#e74c3c", textAlign: "center", padding: "20px 0", marginBottom: "20px" }}>
                No meeting dates available. Please wait for the admin to create meeting dates.
              </div>
            )}
            
            <div style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
            }}>
              <button
                onClick={cancelMeetingDateSelection}
                disabled={selectDateLoading}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  background: "white",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#F9FAFB"}
                onMouseOut={(e) => e.target.style.backgroundColor = "white"}
              >
                Cancel
              </button>
              <button
                onClick={submitMeetingDates}
                disabled={selectDateLoading || !meetingDates.date1 || !meetingDates.date2 || !charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  background: selectDateLoading || !meetingDates.date1 || !meetingDates.date2 || !charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated ? "#9CA3AF" : "#2563EB",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: selectDateLoading || !meetingDates.date1 || !meetingDates.date2 || !charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  if (!selectDateLoading && meetingDates.date1 && meetingDates.date2 && charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated) {
                    e.target.style.backgroundColor = "#1D4ED8";
                  }
                }}
                onMouseOut={(e) => {
                  if (!selectDateLoading && meetingDates.date1 && meetingDates.date2 && charterRequests.find(r => r._id === meetingDateRequestId)?.meetingDatesCreated) {
                    e.target.style.backgroundColor = "#2563EB";
                  }
                }}
              >
                {selectDateLoading ? "Submitting..." : "Submit Dates"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Agreement Confirmation Modal */}
      {showSignAgreementModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
          }}>
            <h3 style={{
              margin: "0 0 16px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
            }}>
              {signAgreementType === 'charterParty' ? 'Sign Charter Party Agreement' : 'Sign Vessel Nomination Agreement'}
            </h3>
            <p style={{
              margin: "0 0 24px 0",
              fontSize: "14px",
              color: "#6B7280",
              lineHeight: "1.5",
            }}>
              {signAgreementType === 'charterParty' 
                ? 'Are you sure you want to sign the charter party agreement? This action cannot be undone.'
                : 'Are you sure you want to sign the vessel nomination agreement? This action cannot be undone.'}
            </p>
            <div style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
            }}>
              <button
                onClick={cancelSignAgreement}
                disabled={signAgreementLoading || signCharterPartyAgreementLoading}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  background: "white",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#F9FAFB"}
                onMouseOut={(e) => e.target.style.backgroundColor = "white"}
              >
                Cancel
              </button>
              <button
                onClick={confirmSignAgreement}
                disabled={signAgreementType === 'charterParty' ? signCharterPartyAgreementLoading : signAgreementLoading}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  background: (signAgreementType === 'charterParty' ? signCharterPartyAgreementLoading : signAgreementLoading) ? "#9CA3AF" : "#16A34A",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: (signAgreementType === 'charterParty' ? signCharterPartyAgreementLoading : signAgreementLoading) ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  const isLoading = signAgreementType === 'charterParty' ? signCharterPartyAgreementLoading : signAgreementLoading;
                  if (!isLoading) {
                    e.target.style.backgroundColor = "#15803D";
                  }
                }}
                onMouseOut={(e) => {
                  const isLoading = signAgreementType === 'charterParty' ? signCharterPartyAgreementLoading : signAgreementLoading;
                  if (!isLoading) {
                    e.target.style.backgroundColor = "#16A34A";
                  }
                }}
              >
                {(signAgreementType === 'charterParty'
                  ? (signCharterPartyAgreementLoading ? 'Processing...' : 'Yes, Sign Charter Party')
                  : (signAgreementLoading ? 'Processing...' : 'Yes, Sign Vessel Nomination'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OngoingProcess;  